import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "nativewind/jsx-runtime";
import React, { useCallback, useEffect, useRef } from 'react';
import { View, Text, TextInput } from 'react-native';
import styled from 'styled-components/native';
import SccPressable from '@/components/SccPressable';
import SccRemoteImage from '@/components/SccRemoteImage';
import CharacterWheelyIcon from '@/assets/icon/character_wheely.svg';
import { color } from '@/constant/color';
import { useLogger } from '@/logging/useLogger';
import { LogParamsProvider } from '@/logging/LogParamsProvider';
import { useEditMode } from '../context/EditModeContext';
import { useResponsive } from '../context/ResponsiveContext';
import ImageUploader from '../components/ImageUploader';
export default function HeaderSection({ titleImageUrl, mobileTitleImageUrl, headerBackgroundImageUrl, mobileHeaderBackgroundImageUrl, headerImageCaption, lastUpdatedDate, wheelchairUserCommentHtml, wheelchairUserCommentHtmlMobile, titleImageWidth, mobileTitleImageWidth, wheelchairUserCommentLabel, mobileWheelchairUserCommentLabel, }) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const editContext = useEditMode();
    const isEditMode = (_a = editContext === null || editContext === void 0 ? void 0 : editContext.isEditMode) !== null && _a !== void 0 ? _a : false;
    const { isDesktop } = useResponsive();
    const logger = useLogger();
    const loggerRef = useRef(logger);
    loggerRef.current = logger;
    // edit mode에서는 editContext.data에서 읽어야 실시간 반영됨
    const currentHeaderBackgroundImageUrl = isEditMode
        ? (_b = editContext === null || editContext === void 0 ? void 0 : editContext.data) === null || _b === void 0 ? void 0 : _b.headerBackgroundImageUrl
        : headerBackgroundImageUrl;
    const currentMobileHeaderBackgroundImageUrl = isEditMode
        ? (_c = editContext === null || editContext === void 0 ? void 0 : editContext.data) === null || _c === void 0 ? void 0 : _c.mobileHeaderBackgroundImageUrl
        : mobileHeaderBackgroundImageUrl;
    const currentMobileTitleImageUrl = isEditMode
        ? (_d = editContext === null || editContext === void 0 ? void 0 : editContext.data) === null || _d === void 0 ? void 0 : _d.mobileTitleImageUrl
        : mobileTitleImageUrl;
    // 데스크탑이 아니면 모바일 이미지 우선 사용
    const displayBackgroundImageUrl = isDesktop
        ? currentHeaderBackgroundImageUrl
        : (currentMobileHeaderBackgroundImageUrl || currentHeaderBackgroundImageUrl);
    const displayTitleImageUrl = isDesktop
        ? titleImageUrl
        : (currentMobileTitleImageUrl || titleImageUrl);
    const currentLastUpdatedDate = isEditMode
        ? (_e = editContext === null || editContext === void 0 ? void 0 : editContext.data) === null || _e === void 0 ? void 0 : _e.lastUpdatedDate
        : lastUpdatedDate;
    const currentWheelchairUserCommentHtml = isEditMode
        ? (_f = editContext === null || editContext === void 0 ? void 0 : editContext.data) === null || _f === void 0 ? void 0 : _f.wheelchairUserCommentHtml
        : wheelchairUserCommentHtml;
    const currentWheelchairUserCommentHtmlMobile = isEditMode
        ? (_g = editContext === null || editContext === void 0 ? void 0 : editContext.data) === null || _g === void 0 ? void 0 : _g.wheelchairUserCommentHtmlMobile
        : wheelchairUserCommentHtmlMobile;
    // 데스크탑이 아니면 모바일 HTML 우선 사용
    const displayWheelchairUserCommentHtml = isDesktop
        ? currentWheelchairUserCommentHtml
        : (currentWheelchairUserCommentHtmlMobile || currentWheelchairUserCommentHtml);
    const currentHeaderImageCaption = isEditMode
        ? (_h = editContext === null || editContext === void 0 ? void 0 : editContext.data) === null || _h === void 0 ? void 0 : _h.headerImageCaption
        : headerImageCaption;
    const handleTitleImageChange = useCallback((url) => {
        if (!editContext)
            return;
        editContext.updateData((prev) => (Object.assign(Object.assign({}, prev), { titleImageUrl: url })));
    }, [editContext]);
    const handleBackgroundImageChange = useCallback((url) => {
        if (!editContext)
            return;
        editContext.updateData((prev) => (Object.assign(Object.assign({}, prev), { headerBackgroundImageUrl: url })));
    }, [editContext]);
    const handleLastUpdatedDateChange = useCallback((text) => {
        if (!editContext)
            return;
        editContext.updateData((prev) => (Object.assign(Object.assign({}, prev), { lastUpdatedDate: text })));
    }, [editContext]);
    // 이미지 노출 로깅
    useEffect(() => {
        if (!isEditMode && displayBackgroundImageUrl) {
            loggerRef.current.logElementView('bbucle-road-header-background-image', {
                imageUrl: displayBackgroundImageUrl,
                isDesktop,
            });
        }
    }, [displayBackgroundImageUrl, isEditMode, isDesktop]);
    useEffect(() => {
        if (!isEditMode && displayTitleImageUrl) {
            loggerRef.current.logElementView('bbucle-road-header-title-image', {
                imageUrl: displayTitleImageUrl,
                isDesktop,
            });
        }
    }, [displayTitleImageUrl, isEditMode, isDesktop]);
    return (_jsx(LogParamsProvider, { params: { displaySectionName: '헤더' }, children: _jsxs(Container, { isDesktop: isDesktop, children: [displayBackgroundImageUrl && (_jsxs(_Fragment, { children: [_jsx(BackgroundWrapper, { as: SccPressable, onPress: () => { }, elementName: "bbucle-road-header-background-image-click", logParams: { imageUrl: displayBackgroundImageUrl }, disableLogging: isEditMode, children: _jsx(SccRemoteImage, { imageUrl: displayBackgroundImageUrl, resizeMode: "cover", style: { width: '100%', height: '100%' }, wrapperBackgroundColor: null }) }), _jsx(BackgroundOverlay, { pointerEvents: "none" })] })), currentHeaderImageCaption && (_jsx(ImageCaptionWrapper, { isDesktop: isDesktop, children: _jsx(ImageCaptionText, { isDesktop: isDesktop, children: currentHeaderImageCaption }) })), isEditMode && (_jsx(BackgroundEditOverlay, { children: _jsx(ImageUploader, { currentImageUrl: currentHeaderBackgroundImageUrl, onUploadComplete: handleBackgroundImageChange, compact: true }) })), _jsxs(ContentWrapper, { isDesktop: isDesktop, children: [_jsx(SubTitle, { isDesktop: isDesktop, children: "\uBFCC\uD074\uB85C\uB4DC \uD2B9\uBCC4 \uAE30\uD68D" }), _jsxs(TitleImageWrapper, { isDesktop: isDesktop, customWidth: isDesktop ? titleImageWidth : mobileTitleImageWidth, children: [isEditMode && (_jsx(EditImageOverlay, { children: _jsx(ImageUploader, { currentImageUrl: titleImageUrl, onUploadComplete: handleTitleImageChange, compact: true }) })), displayTitleImageUrl ? (_jsx(SccPressable, { onPress: () => { }, elementName: "bbucle-road-header-title-image-click", logParams: { imageUrl: displayTitleImageUrl }, disableLogging: isEditMode, children: _jsx(SccRemoteImage, { imageUrl: displayTitleImageUrl, resizeMode: "contain", wrapperBackgroundColor: null }) })) : (isEditMode && (_jsx(EmptyImagePlaceholder, { children: _jsx(EmptyImageText, { children: "\uD0C0\uC774\uD2C0 \uC774\uBBF8\uC9C0\uB97C \uC5C5\uB85C\uB4DC\uD558\uC138\uC694" }) })))] }), isEditMode ? (_jsx(UpdateDateBadge, { isDesktop: isDesktop, children: _jsx(UpdateDateInput, { value: currentLastUpdatedDate || '', onChangeText: handleLastUpdatedDateChange, placeholder: "\uCD5C\uC885 \uC5C5\uB370\uC774\uD2B8 YYYY.MM.DD", placeholderTextColor: color.gray40 }) })) : (currentLastUpdatedDate && (_jsx(UpdateDateBadge, { isDesktop: isDesktop, children: _jsx(UpdateDateText, { isDesktop: isDesktop, children: currentLastUpdatedDate }) }))), _jsxs(CommentSection, { isDesktop: isDesktop, children: [_jsx(CommentLabel, { isDesktop: isDesktop, children: isDesktop
                                        ? (wheelchairUserCommentLabel || '휠체어 사용자의 고척돔 접근성 한마디')
                                        : (mobileWheelchairUserCommentLabel || wheelchairUserCommentLabel || '휠체어 사용자의 고척돔 접근성 한마디') }), _jsxs(SpeechBubbleWithCharacter, { children: [_jsxs(SpeechBubbleWrapper, { isDesktop: isDesktop, children: [_jsx(SpeechBubble, { isDesktop: isDesktop, children: displayWheelchairUserCommentHtml ? (_jsx(CommentHtmlWrapper, { isDesktop: isDesktop, children: _jsx("div", { dangerouslySetInnerHTML: {
                                                                __html: displayWheelchairUserCommentHtml,
                                                            } }) })) : (isEditMode && (_jsx(EmptyCommentPlaceholder, { children: "\uC0AC\uC774\uB4DC\uBC14\uC5D0\uC11C \uC811\uADFC\uC131 \uD55C\uB9C8\uB514\uB97C \uC785\uB825\uD558\uC138\uC694" }))) }), _jsx(SpeechBubbleTail, {})] }), _jsx(CharacterWrapper, { isDesktop: isDesktop, children: _jsx(CharacterWheelyIcon, { width: isDesktop ? 89 : 60, height: isDesktop ? 86 : 58, viewBox: "0 0 89 86" }) })] })] })] })] }) }));
}
const Container = styled(View) `
  position: relative;
  background-color: #b2d7ff;
  overflow: hidden;
`;
const BackgroundWrapper = styled(View) `
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`;
const BackgroundOverlay = styled(View) `
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.4);
`;
const ImageCaptionWrapper = styled(View) `
  position: absolute;
  bottom: ${({ isDesktop }) => (isDesktop ? '20px' : '16px')};
  right: ${({ isDesktop }) => (isDesktop ? '24px' : '16px')};
  z-index: 2;
`;
const ImageCaptionText = styled(Text) `
  font-family: Pretendard;
  font-size: ${({ isDesktop }) => (isDesktop ? '13px' : '12px')};
  line-height: ${({ isDesktop }) => (isDesktop ? '18px' : '16px')};
  font-weight: 400;
  color: ${color.gray35};
`;
const BackgroundEditOverlay = styled(View) `
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 10;
`;
const ContentWrapper = styled(View) `
  align-items: center;
  padding-top: ${({ isDesktop }) => (isDesktop ? '119px' : '80px')};
  z-index: 1;
`;
const SubTitle = styled(Text) `
  font-family: Pretendard;
  font-size: ${({ isDesktop }) => (isDesktop ? '24px' : '20px')};
  font-weight: 700;
  line-height: ${({ isDesktop }) => (isDesktop ? '32px' : '28px')};
  color: ${color.white};
  text-align: center;
  margin-bottom: 12px;
`;
const TitleImageWrapper = styled(View) `
  position: relative;
  width: ${({ isDesktop, customWidth }) => customWidth ? `${customWidth}px` : (isDesktop ? '487px' : '280px')};
`;
const EditImageOverlay = styled(View) `
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
`;
const EmptyImagePlaceholder = styled(View) `
  flex: 1;
  padding: 40px;
  background-color: rgba(255, 255, 255, 0.2);
  border: 2px dashed rgba(255, 255, 255, 0.5);
  border-radius: 8px;
  align-items: center;
  justify-content: center;
`;
const EmptyImageText = styled(Text) `
  font-size: 14px;
  color: ${color.white};
`;
const UpdateDateBadge = styled(View) `
  flex-direction: row;
  align-items: center;
  background-color: ${color.black};
  padding: ${({ isDesktop }) => (isDesktop ? '4px 16px' : '3px 14px')};
  border-radius: 100px;
  margin-top: 12px;
  margin-bottom: ${({ isDesktop }) => (isDesktop ? '60px' : '40px')};
`;
const UpdateDateText = styled(Text) `
  font-family: Pretendard;
  font-size: ${({ isDesktop }) => (isDesktop ? '16px' : '14px')};
  font-weight: 500;
  line-height: ${({ isDesktop }) => (isDesktop ? '24px' : '18px')};
  color: ${color.white};
`;
const UpdateDateInput = styled(TextInput) `
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 500;
  color: ${color.white};
  min-width: 80px;
  padding: 0;
`;
const CommentSection = styled(View) `
  align-items: center;
  gap: 12px;
  max-width: ${({ isDesktop }) => (isDesktop ? '600px' : '90%')};
`;
const CommentLabel = styled(Text) `
  font-family: Pretendard;
  font-size: ${({ isDesktop }) => (isDesktop ? '18px' : '18px')};
  font-weight: 600;
  line-height: ${({ isDesktop }) => (isDesktop ? '26px' : '26px')};
  letter-spacing: -0.36px;
  color: ${color.white};
  text-align: center;
  white-space: pre-line;
`;
const SpeechBubbleWrapper = styled(View) `
  width: ${({ isDesktop }) => (isDesktop ? '537px' : '308px')};
`;
const SpeechBubble = styled(View) `
  background-color: ${color.white};
  padding: ${({ isDesktop }) => (isDesktop ? '14px 16px' : '12px 14px')};
  border-radius: 12px;
`;
const SpeechBubbleTail = styled(View) `
  width: 0;
  height: 0;
  border-left-width: 10px;
  border-right-width: 10px;
  border-top-width: 12px;
  border-left-color: transparent;
  border-right-color: transparent;
  border-top-color: ${color.white};
  margin-left: 40px;
`;
const CommentHtmlWrapper = styled(View) `
  font-family: Pretendard;
  font-size: ${({ isDesktop }) => (isDesktop ? '16px' : '14px')};
  font-weight: 400;
  line-height: ${({ isDesktop }) => (isDesktop ? '24px' : '22px')};
  letter-spacing: -0.75px;
  color: ${color.black};
`;
const EmptyCommentPlaceholder = styled(Text) `
  font-family: Pretendard;
  font-size: 14px;
  color: ${color.gray40};
`;
const SpeechBubbleWithCharacter = styled(View) `
  align-items: flex-start;
`;
const CharacterWrapper = styled(View) `
  margin-top: 16px;
  margin-left: ${({ isDesktop }) => (isDesktop ? '-10px' : '10px')};
`;
